"use client";

import * as React from "react";
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react";
import { cn } from "@/components/utils";
import { Badge } from "./Badge";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "./Command";

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectProps {
  options: MultiSelectOption[];
  value?: string[];
  onChange?: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  maxSelections?: number;
  searchable?: boolean;
}

export function MultiSelect({
  options,
  value = [],
  onChange,
  placeholder = "선택하세요",
  disabled = false,
  className,
  maxSelections,
  searchable = true,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");

  const filteredOptions = React.useMemo(() => {
    if (!searchable || !search) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [options, search, searchable]);

  const selectedOptions = React.useMemo(() => {
    return options.filter((option) => value.includes(option.value));
  }, [options, value]);

  const handleSelect = (optionValue: string) => {
    if (disabled) return;

    const isCurrentlySelected = value.includes(optionValue);

    if (isCurrentlySelected) {
      // 이미 선택된 항목이면 제거
      const newValue = value.filter((v) => v !== optionValue);
      onChange?.(newValue);
    } else {
      // 새로운 항목을 추가하려고 할 때만 maxSelections 체크
      if (maxSelections && value.length >= maxSelections) {
        return; // 최대 선택 개수에 도달했으면 추가하지 않음
      }
      const newValue = [...value, optionValue];
      onChange?.(newValue);
    }
  };

  const handleRemove = (optionValue: string) => {
    if (disabled) return;
    const newValue = value.filter((v) => v !== optionValue);
    onChange?.(newValue);
  };

  const handleClear = () => {
    if (disabled) return;
    onChange?.([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div
          role="combobox"
          aria-expanded={open}
          className={cn(
            "flex w-full items-center justify-between rounded-lg border border-input bg-white px-3 py-2 text-sm transition-all duration-200",
            "hover:border-emerald-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-200",
            "min-h-10 h-auto",
            disabled &&
              "cursor-not-allowed opacity-50 bg-slate-100 text-slate-500 border-slate-300",
            className
          )}
        >
          <div className="flex flex-wrap gap-1 flex-1">
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : (
              selectedOptions.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="text-xs"
                >
                  {option.label}
                  {!disabled && (
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemove(option.value);
                      }}
                      className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5 cursor-pointer"
                    >
                      <XIcon className="h-3 w-3" />
                    </span>
                  )}
                </Badge>
              ))
            )}
          </div>
          <div className="flex items-center gap-1">
            {selectedOptions.length > 0 && !disabled && (
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  handleClear();
                }}
                className="hover:bg-destructive hover:text-destructive-foreground rounded p-0.5 cursor-pointer"
              >
                <XIcon className="h-4 w-4" />
              </span>
            )}
            <ChevronDownIcon className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0" align="start">
        <Command>
          {searchable && (
            <CommandInput
              placeholder="검색..."
              value={search}
              onValueChange={setSearch}
            />
          )}
          <CommandList>
            <CommandEmpty>검색 결과가 없습니다.</CommandEmpty>
            <CommandGroup>
              {filteredOptions.map((option) => {
                const isSelected = value.includes(option.value);
                const isDisabled = Boolean(
                  maxSelections && value.length >= maxSelections && !isSelected
                );

                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() => handleSelect(option.value)}
                    onClick={() => handleSelect(option.value)}
                    disabled={isDisabled}
                    className={cn(
                      "cursor-pointer",
                      isDisabled && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <div className="flex items-center gap-2 w-full">
                      <div
                        className={cn(
                          "flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : "opacity-50 [&_svg]:invisible"
                        )}
                      >
                        <CheckIcon className="h-3 w-3" />
                      </div>
                      <span className="flex-1">{option.label}</span>
                      {isDisabled && (
                        <span className="text-xs text-muted-foreground">
                          (최대 {maxSelections}개)
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
